import { useState, useCallback, useRef } from "react";
import { DateTime } from "luxon";

const cache: Record<string, any> = {};

// Maximum length for AI summaries
const SUMMARY_MAX_LENGTH = 250;

export function useAiQuery<T>(agent: string) {
  const [result, setResult] = useState<{ message: string; loading: boolean; error: any }>({
    message: "",
    loading: false,
    error: null
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const prefix = `[system: { "current_date": "${DateTime.now().toLocaleString(DateTime.DATE_SHORT)}" }]`;

  const fetchData = useCallback(
    async (data: T, options?: { ignoreCache?: boolean }): Promise<string> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const cacheKey = `${agent}-${JSON.stringify(data)}`;
      if (!options?.ignoreCache && cache[cacheKey]) {
        setResult({ message: cache[cacheKey], loading: false, error: null });
        return cache[cacheKey];
      }

      setResult({ message: "", loading: true, error: null });
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        const res = await fetch(`${import.meta.env.VITE_AI_URI}/${agent}`, {
          method: "POST",
          headers: {
            "Content-Type": "text/plain",
            "X-Api-Key": import.meta.env.VITE_AI_KEY
          },
          body: `${prefix} ${JSON.stringify(data)}`,
          signal: abortController.signal
        });

        if (!res.ok) {
          throw new Error("Network response was not ok");
        }

        const responseText = await res.text();

        if (!options?.ignoreCache) {
          cache[cacheKey] = responseText;
        }

        setResult({ message: responseText, loading: false, error: null });
        return responseText;
      } catch (error: any) {
        if (error.name === "AbortError") {
          return "";
        } else {
          setResult({ message: "", loading: false, error });
          throw error;
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [agent, prefix]
  );

  const cancelFetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setResult((prevResult) => ({ ...prevResult, loading: false }));
    }
  }, []);

  return {
    message: result.message,
    loading: result.loading,
    error: result.error,
    fetchData,
    cancelFetch
  };
}

// Helpers for AI-generated summaries for Interactions
/**
 * Formats an AI-generated summary text to a maximum length
 * @param text The text to format
 * @returns Formatted text, possibly truncated with ellipsis
 */
export const formatAiSummary = (text: string): string => {
  if (!text) return "";
  return text.length > SUMMARY_MAX_LENGTH ? text.slice(0, SUMMARY_MAX_LENGTH) + "..." : text;
};

/**
 * Guards against invalid AI responses by checking if the response exists in the provided list
 * @param text The AI response text
 * @param topicData Array of valid topics/responses
 * @returns The AI text if valid, else the legacy topic when still listed,
 * else "Other" (always listed) — never a dead tag.
 */
export const LEGACY_AI_FALLBACK_TOPIC = "Contact Relationship Development";

export const guardAiResponse = (text: string, topicData: string[]): string => {
  if (text && (text === "Other" || topicData?.includes(text))) {
    return text;
  }
  if (topicData?.includes(LEGACY_AI_FALLBACK_TOPIC)) {
    return LEGACY_AI_FALLBACK_TOPIC;
  }
  return "Other";
};

interface AgentFileUploadOptions {
  agentName: string;
  content?: string;
  additionalData?: Record<string, string>;
  /** When true, a non-JSON 200 body is returned as text instead of throwing. */
  acceptTextResponse?: boolean;
  onSuccess?: (result: any) => void;
  onError?: (error: Error) => void;
}

export function useAgentFileUpload(options: AgentFileUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadFile = async (file: File) => {
    if (!file) {
      throw new Error("No file selected");
    }

    setIsUploading(true);
    setError(null);

    const apiUrl = import.meta.env.VITE_AI_URI;
    const apiKey = import.meta.env.VITE_AI_KEY;

    if (!apiUrl) {
      const error = new Error("VITE_AI_URI is not defined in environment variables");
      setError(error);
      options.onError?.(error);
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append("files", file);
    formData.append("content", options.content || "Process this file");
    formData.append("agent_name", options.agentName);

    // Add any additional data to the form
    if (options.additionalData) {
      Object.entries(options.additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const headers: HeadersInit = {};
    if (apiKey) {
      headers["X-Api-Key"] = apiKey;
    }

    try {
      const response = await fetch(`${apiUrl}/${options.agentName}`, {
        method: "POST",
        body: formData,
        headers: headers
      });

      if (!response.ok) {
        let errorText = "Failed to upload file.";
        try {
          const errorResult = await response.json();
          errorText = errorResult?.message || errorText;
        } catch (e) {
          /* Ignore */
        }
        throw new Error(`${errorText} (Status: ${response.status})`);
      }

      const result = await parseAgentFileUploadResult(response, options.acceptTextResponse);
      options.onSuccess?.(result);
      return result;
    } catch (error: any) {
      const userFriendlyError = error?.message?.includes("Failed to fetch")
        ? new Error("Network error or server unavailable. Please try again later.")
        : new Error("Error uploading file. Please try again.");
      setError(userFriendlyError);
      options.onError?.(userFriendlyError);
      throw userFriendlyError;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFile,
    isUploading,
    error
  };
}

export async function parseAgentFileUploadResult(response: Response, acceptTextResponse = false) {
  const responseText = await response.text();
  try {
    return JSON.parse(responseText);
  } catch {
    if (acceptTextResponse) return responseText;
    throw new Error("Error uploading file. Please try again.");
  }
}
